<i18n>
{
  "en": {
    "noPermission": "You have no permission",
    "noInput": "Please input email, userId or licenseId",
    "noEmail": "Can't find email",
    "noUserId": "Can't find userId",
    "noLicenseId": "Can't find licenseId",
    "noLicenses": "Find User!. But this user has no any license or already expired"
  },
  "zhHant": {
    "noPermission": "權限不足",
    "noInput": "請輸入 email, userId 或是 licenseId",
    "noEmail": "找不到此 email",
    "noUserId": "找不到此 userId",
    "noLicenseId": "找不到此 licenseId",
    "noLicenses": "有找到此使用者！但使用者沒有任何License或是已經過期"
  },
  "ja": {
    "noPermission": "You have no permission",
    "noInput": "Please input email, userId or licenseId",
    "noEmail": "Can't find email",
    "noUserId": "Can't find userId",
    "noLicenseId": "Can't find licenseId",
    "noLicenses": "Find User!. But this user has no any license or already expired"
  }
}
</i18n>

<template>
  <v-container grid-list-md text-xs-center text-md-center fluid>
    <v-layout row wrap align-center>
      <v-flex xs12 md6>
        <v-text-field v-model="email" placeholder="email, userId, licenseId" @keydown.enter="search"
          @input="onUserInput"></v-text-field>
      </v-flex>
      <v-flex xs3 md2>
        <v-checkbox v-model="includePast" label="包含過期"></v-checkbox>
      </v-flex>
      <v-flex xs5 md2>
        <v-radio-group v-model="licenseType" :disabled="includePast === false">
          <v-radio label="subscription" value="subscription"></v-radio>
          <v-radio label="trial" value="trial"></v-radio>
          <v-radio label="bonus" value="bonus"></v-radio>
          <v-radio label="test" value="test"></v-radio>
        </v-radio-group>
      </v-flex>
      <v-flex xs4 md2>
        <v-btn @click="search" :loading="loading" :disabled="loading">Search</v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md6 v-for="userInfo in userInfos" :key="userInfo.uid">
        <v-card>
          <v-layout>
            <v-flex cols="4">
              <v-img class="ml-3" v-if="userInfo.photoURL" :src="userInfo.photoURL" height="125px" contain></v-img>
            </v-flex>
            <v-flex cols="8">
              <v-card-title>
                <div>
                  <div>{{ userInfo.providerId }}</div>
                  <div>{{ userInfo.displayName }}</div>
                  <div>{{ userInfo.email }}</div>
                  <div>{{ userInfo.phoneNumber }}</div>
                  <div>User ID: {{ userId }}</div>
                </div>
              </v-card-title>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md6 lg4 xl3 v-for="license in licenses" :key="license.licenseId" row wrap>
        <div>
          <v-card>
            <v-card-title style="text-align: left">
              <table>
                <tr>
                  <td><strong>User ID:</strong></td>
                  <td>{{ license.userId }}</td>
                </tr>
                <tr>
                  <td><strong>License ID:</strong></td>
                  <td>{{ license.licenseId }}</td>
                </tr>
                <tr>
                  <td><strong>Expired Time:</strong></td>
                  <td>{{ new Date(license.expireTime * 1000).toLocaleString() }}</td>
                </tr>
                <tr>
                  <td><strong>License Type:</strong></td>
                  <td>{{ license.type }}</td>
                </tr>
                <tr>
                  <td><strong>License Tier:</strong></td>
                  <td>{{ license.tier }}</td>
                </tr>
                <tr>
                  <td><strong>Support Game:</strong></td>
                  <td>{{ license.supportGame }}</td>
                </tr>
                <tr>
                  <td><strong>Task:</strong></td>
                  <td>{{ license.task }}</td>
                </tr>
                <tr>
                  <td><strong>Task Status:</strong></td>
                  <td>{{ license.taskStatus }}</td>
                </tr>
                <tr>
                  <td><strong>Subscription Status:</strong></td>
                  <td>
                    <span v-if="license.isCanceled === false">Active</span>
                    <span v-else-if="license.isCanceled === true">Canceled</span>
                    <span v-else>Unknown</span>
                    -
                    <v-btn v-if="license.isCanceled === false" @click="cancelSubscription(license)" small color="red"
                      outline>
                      取消訂閱
                    </v-btn>
                    <span v-else-if="license.isCanceled === true">Already Canceled</span>
                    <span v-else>N/A</span>
                  </td>
                </tr>
                <tr>
                  <td><strong>Game Session:</strong></td>
                  <td>
                    <v-btn v-if="license.taskConfig !== null" small @click="logoutGameAccount(license.licenseId)"
                      :loading="isGameAccountSigningOut" :disabled="isGameAccountSigningOut">
                      LOGOUT GAME SESSION
                    </v-btn>
                    <span v-else>No Active Session</span>
                  </td>
                </tr>
              </table>
            </v-card-title>

            <v-card-text v-if="!isEmpty(license.taskConfig)" style="text-align: left">
              <v-subheader @click="toggleTaskConfig(license)" style="cursor: pointer;">
                Task Config {{ license.showTaskConfig ? '[-]' : '[+]' }}
              </v-subheader>
              <div v-if="license.showTaskConfig">
                <v-layout v-for="(value, key) in license.taskConfig" :key="key">
                  <v-flex xs6>{{ key }}</v-flex>
                  <v-flex xs6 style="overflow: hidden">{{ value }}</v-flex>
                </v-layout>
              </div>
            </v-card-text>

            <div v-if="license.payments !== undefined">
              <v-list v-for="(payment, idx) in license.payments" :key="payment.id">
                <div v-if="payment.source === 'PayPal'">
                  <div v-if="idx === 0">
                    <v-divider></v-divider>
                    <v-subheader>PayPal Payment</v-subheader>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Subscription ID</v-list-tile-title>
                        <v-list-tile-sub-title>
                          {{ payment.payload.resource.billing_agreement_id }}
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-sub-title>
                          <a :href="`https://www.paypal.com/billing/subscriptions/${payment.payload.resource.billing_agreement_id}`"
                            target="_blank">Go Paypal Subscribe Page</a>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </div>

                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        ({{ idx + 1 }}) Transaction
                        <span v-if="payment.success === true">${{ payment.payload.resource.amount.total }}
                          {{ payment.payload.resource.amount.currency }}</span>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{ new Date(payment.createTime * 1000).toLocaleString() }}
                      </v-list-tile-sub-title>
                      <v-list-tile-sub-title v-if="payment.success === true">
                        {{ payment.payload.resource.id }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </div>

                <div v-else-if="payment.source === 'Stripe'">
                  <div v-if="idx === 0">
                    <v-divider></v-divider>
                    <v-subheader>Stripe Payment</v-subheader>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Subscription ID</v-list-tile-title>
                        <v-list-tile-sub-title>
                          {{ payment.payload.data.object.subscription }}
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-sub-title>
                          <a :href="`https://dashboard.stripe.com/subscriptions/${payment.payload.data.object.subscription}`"
                            target="_blank">Go Stripe Subscription Page</a>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </div>

                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        ({{ idx + 1 }}) Transaction
                        <span v-if="payment.success === true">${{
                          isStripeZeroDecimal(payment.payload.data.object.currency)
                            ? payment.payload.data.object.total
                            : payment.payload.data.object.total / 100
                        }}
                          {{ payment.payload.data.object.currency }}</span>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{ new Date(payment.createTime * 1000).toLocaleString() }}
                      </v-list-tile-sub-title>
                      <v-list-tile-sub-title v-if="payment.success === true">
                        {{ payment.payload.data.object.payment_intent }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </div>

                <div v-else>
                  <div v-if="idx === 0">
                    <v-divider></v-divider>
                    <v-subheader>ECPay Payment</v-subheader>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>MerchantTradeNo</v-list-tile-title>
                        <v-list-tile-sub-title>
                          {{ payment.payload.MerchantTradeNo }}
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-sub-title>
                          <a :href="`https://vendor.ecpay.com.tw/TradeCreditPeriod/TradeCreditPeriodQuery`"
                            target="_blank">Go Ecpay Subscribe Page</a>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </div>

                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        ({{ idx + 1 }}) Transaction ${{ payment.payload.Amount || payment.payload.amount }}
                        NTD
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{ new Date(payment.createTime * 1000).toLocaleString() }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </div>
              </v-list>
            </div>
          </v-card>

          <v-card v-if="license.device !== undefined">
            <v-list>
              <v-subheader>Device Info ({{ license.device.deviceId }})</v-subheader>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title>Last Ping Time</v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{ new Date(license.device.lastPingTime * 1000).toLocaleString() }}
                    ({{ Math.floor(Date.now() / 1000) - license.device.lastPingTime }}
                    sec)
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <img style="width: 100%" :src="`data:image/png;base64, ${license.device.screenshotBase64}`" />
            </v-list>
          </v-card>
          <v-card v-else>Device Not Found</v-card>
        </div>
      </v-flex>
    </v-layout>
    <v-dialog v-model="confirmDialog" persistent max-width="400">
      <v-card>
        <v-card-text>確定取消此訂閱？</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small color="red" outline @click="unsubscribe" :loading="confirmLoading"> 確定取消 </v-btn>
          <v-btn small color="primary" @click="
            confirmDialog = false;
          cancelSubscriptionLicense = null;
          ">
            放棄取消
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { isStripeZeroDecimal } from '../misc/util';
import isEmpty from 'lodash/isEmpty';
import firebase from 'firebase/app';
import 'firebase/functions';

const getUserInfo = firebase.functions().httpsCallable('xAdminAPI-getUserInfo');

export default {
  data() {
    return {
      email: '',
      licenses: [],
      userInfos: [],
      userId: '',
      loading: false,
      toCancelLicense: null,
      confirmDialog: false,
      confirmLoading: false,
      includePast: false,
      licenseType: undefined,
      isGameAccountSigningOut: false,
    };
  },
  async mounted() {
    const querySearch = this.$route.query.search;
    this.email = querySearch || '';
    if (this.email !== '') {
      await this.search();
    }
  },
  destroyed() {
    this.$router.replace({ query: {} });
  },
  methods: {
    isStripeZeroDecimal,
    async unsubscribe() {
      if (this.toCancelLicense === null) {
        return;
      }
      this.confirmLoading = true;
      try {
        console.log({
          userId: this.toCancelLicense.userId,
          licenseId: this.toCancelLicense.licenseId,
        });
        await firebase.functions().httpsCallable('xAdminAPI-cancelSubscription')({
          userId: this.toCancelLicense.userId,
          licenseId: this.toCancelLicense.licenseId,
        });
        this.$gtag.event('subscription', 'cancel', this.toCancelLicense.userId, 1);
      } catch (e) {
        alert(e.message);
      }
      this.confirmLoading = false;
      this.confirmDialog = false;
      this.toCancelLicense = null;
      await this.search();
    },
    cancelSubscription(license) {
      this.confirmDialog = true;
      this.toCancelLicense = license;
    },
    isEmpty(x) {
      return isEmpty(x);
    },
    toggleTaskConfig(license) {
      this.$set(license, 'showTaskConfig', !license.showTaskConfig);
    },
    onUserInput() {
      this.$router.replace({ query: { search: this.email } })
    },
    async search() {
      this.email = this.email.trim();
      this.licenses.splice(0, this.licenses.length);
      this.userInfos.splice(0, this.userInfos.length);
      this.userId = '';
      if (this.email === '') {
        return;
      }
      let result;
      this.loading = true;
      try {
        const queryData = {
          includePast: this.includePast,
          licenseType: this.licenseType,
        };
        if (this.email.indexOf('@') !== -1) {
          queryData['email'] = this.email;
        } else if (this.email.length === 36) {
          queryData['licenseId'] = this.email;
        } else {
          queryData['userId'] = this.email;
        }
        result = await getUserInfo(queryData);
      } catch (e) {
        this.loading = false;
        alert(this.$t(e.message));
        return;
      }
      this.loading = false;
      const [userLicenses, userInfos, userId] = result.data;
      for (const licenseId in userLicenses) {
        this.licenses.push(userLicenses[licenseId]);
      }
      console.log(userInfos);
      this.userInfos = userInfos;
      this.userId = userId;
      if (this.licenses.length === 0) {
        alert(this.$t('noLicenses'));
      }
    },
    async logoutGameAccount(licenseId) {
      if (!confirm('確定要登出遊戲帳號嗎？')) {
        return;
      }

      this.isGameAccountSigningOut = true;
      try {
        const success = await firebase.functions().httpsCallable('XRobotmonGroup-xDeleteLoginCache')({
          licenseId: licenseId,
        });
        console.log(`logoutGameAccount`, success);
      } catch (e) {
        console.log(`logoutGameAccount`, e.message);
      } finally {
        this.isGameAccountSigningOut = false;
        alert('delete session success');
      }
    },
  },
  watch: {
    includePast: function (newVal) {
      if (newVal === false) {
        this.licenseType = undefined;
      }
      if (newVal === true) {
        this.licenseType = 'subscription';
      }
    },
  },
};
</script>